/**
 * ActionCenter — unified operational surface consumed by:
 *
 *   /actions            (admin, standalone page)
 *   MarketingDashboard  (tab "Centro de acción")
 *   ConstructorDashboard(tab "Action Center")
 *
 * Also renders in `compact` mode for the dashboard "Resumen" section.
 *
 * Design rules from the plan (sections 3-8):
 *   - counts are ALWAYS shown from the global pool (backend contract).
 *   - chips filter items client-side; counts never change.
 *   - INFO items are hidden by default; ?urgency=INFO is not exposed
 *     via the UI in this PR.
 *   - compact: max 5 items, CRITICAL/TODAY/OVERDUE only, "Ver todo".
 *
 * The component does NOT fetch anything when receives `items+counts+…`
 * from a parent (dashboard case). When those props are undefined the
 * component owns its own `useActionCenterData` (standalone /actions
 * page case).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowClockwise, ArrowRight, ClockCounterClockwise, Lightning } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useActionCenterData } from "@/hooks/useActionCenterData";
import { DESKTOP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import ActionCard from "@/components/actionCenter/ActionCard";
import PriorityAiPanel from "@/components/PriorityAiPanel";
import {
  URGENCY_CHIP_ORDER,
  TYPE_TAB_ORDER,
  urgencyStyleFor,
  urgencyLabel,
} from "@/components/actionCenter/urgencyStyle";
import { matchesTypeFilter } from "@/lib/actionFilters";

const COMPACT_LIMIT = 5;
const COMPACT_URGENCIES = new Set(["CRITICAL", "TODAY", "OVERDUE"]);

const I18N = {
  es: {
    title: "Centro de acción",
    subtitle: "Todo lo que necesita tu atención, ordenado por prioridad.",
    needAttention: (n) => `${n} necesitan tu atención`,
    reload: "Recargar",
    seeAll: "Ver todo",
    loading: "Cargando…",
    empty: "Todo en orden.",
    emptySub: "No hay acciones pendientes ahora mismo.",
    filterAll: "Todo",
    error: "No pudimos cargar el Centro de acción.",
    quickActions: "Ver Centro de acción",
    quickFollowUps: "Ver seguimientos",
    quickPending: (n) => (n === 1 ? "1 pendiente" : `${n} pendientes`),
  },
  en: {
    title: "Action Center",
    subtitle: "Everything that needs your attention, ranked by priority.",
    needAttention: (n) => `${n} need attention`,
    reload: "Reload",
    seeAll: "See all",
    loading: "Loading…",
    empty: "All clear.",
    emptySub: "No pending actions right now.",
    filterAll: "All",
    error: "We couldn't load the Action Center.",
    quickActions: "View Action Center",
    quickFollowUps: "View follow-ups",
    quickPending: (n) => (n === 1 ? "1 pending" : `${n} pending`),
  },
};

export default function ActionCenter({
  compact = false,
  lang = "es",
  // Externally-provided data (dashboard case). When any of these is
  // undefined the component fetches its own.
  items: itemsExternal,
  counts: countsExternal,
  loading: loadingExternal,
  error: errorExternal,
  // `reload` powers the "Recargar" button in the header — full refresh
  // (both /actions AND NBA when applicable).
  reload: reloadExternal,
  // `onReloadAfterMutation` refreshes just /actions (skips NBA) after a
  // successful card mutation. Falls back to `reload` when the parent
  // does not distinguish (standalone /actions page has no NBA anyway).
  onReloadAfterMutation: onReloadAfterMutationExternal,
  // Compact-only: parent-supplied handler for the "Ver todo" button. If
  // omitted, the button is hidden (marketing/constructor never get a
  // link to admin-only /actions).
  onViewAll,
  // Highlight a specific action (used when PriorityAiPanel clicks
  // into an item that's visible on the board).
  highlightActionId = null,
}) {
  const t = I18N[lang] || I18N.es;
  const { user } = useAuth();
  const role = user?.role || "admin";

  const owned = itemsExternal === undefined;
  const owner = useActionCenterData({
    lang,
    includeInfo: false,
    withNba: false,
  });
  const items = owned ? owner.items : itemsExternal;
  const counts = owned ? owner.counts : countsExternal;
  const loading = owned ? owner.loading : loadingExternal;
  const error = owned ? owner.error : errorExternal;
  const reload = owned ? owner.reloadAll : reloadExternal;
  const onReloadAfterMutation = owned
    ? owner.reloadActions
    : (onReloadAfterMutationExternal || reloadExternal);

  const [urgencyFilter, setUrgencyFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("all");

  const visibleItems = useMemo(() => {
    let out = items || [];
    // INFO already excluded server-side when include_info=false. Compact
    // also excludes UPCOMING to keep only actionable urgencies.
    if (compact) {
      out = out.filter((it) => COMPACT_URGENCIES.has(it.urgency));
    }
    if (urgencyFilter !== "ALL") {
      out = out.filter((it) => it.urgency === urgencyFilter);
    }
    // Un solo sitio decide qué entra en cada pestaña — incluida
    // "Seguimientos", que es un filtro de interfaz sobre `follow_up`.
    out = out.filter((it) => matchesTypeFilter(it, typeFilter));
    if (compact) return out.slice(0, COMPACT_LIMIT);
    return out;
  }, [items, urgencyFilter, typeFilter, compact]);

  const headerCount = compact
    ? (counts?.critical || 0) + (counts?.today || 0) + (counts?.overdue || 0)
    : counts?.total || 0;

  return (
    <div
      className={`rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 ${compact ? "p-4" : "p-5"}`}
      data-testid={compact ? "action-center-compact" : "action-center-full"}
    >
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">
            {t.title}
          </div>
          <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t.needAttention(headerCount)}
          </div>
          {!compact && (
            <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
              {t.subtitle}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {reload && (
            <button
              type="button"
              onClick={reload}
              className="text-xs text-stone-500 hover:text-stone-800 dark:hover:text-stone-100 inline-flex items-center gap-1"
              data-testid="action-center-reload"
            >
              <ArrowClockwise size={14} />
              {t.reload}
            </button>
          )}
          {compact && onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-900"
              data-testid="action-center-seeall"
            >
              {t.seeAll}
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Chips (urgency) — full mode only */}
      {!compact && (
        <div className="mt-4 flex flex-wrap gap-2">
          <UrgencyChip
            active={urgencyFilter === "ALL"}
            onClick={() => setUrgencyFilter("ALL")}
            label={t.filterAll}
            count={counts?.total || 0}
          />
          {URGENCY_CHIP_ORDER.map((u) => {
            const s = urgencyStyleFor(u);
            const key = u.toLowerCase();
            return (
              <UrgencyChip
                key={u}
                active={urgencyFilter === u}
                onClick={() => setUrgencyFilter(u)}
                label={urgencyLabel(u, lang)}
                count={counts?.[key] || 0}
                style={s}
              />
            );
          })}
        </div>
      )}

      {/* Tabs (type) — full mode only */}
      {!compact && (
        <div className="mt-3 flex flex-wrap gap-1">
          {TYPE_TAB_ORDER.map((tab) => {
            const active = typeFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTypeFilter(tab.key)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                  active
                    ? "bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900"
                    : "text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-900"
                }`}
                data-testid={`action-center-tab-${tab.key}`}
                aria-pressed={active}
              >
                {tab.label[lang] || tab.label.es}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="mt-4">
        {error && (
          <div
            className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300"
            data-testid="action-center-error"
          >
            {t.error}
            <div className="text-xs mt-1 opacity-80">{error}</div>
          </div>
        )}
        {!error && loading && (
          <div className="text-sm text-stone-500 py-6 text-center" data-testid="action-center-loading">
            {t.loading}
          </div>
        )}
        {!error && !loading && visibleItems.length === 0 && (
          <div className="text-center py-8" data-testid="action-center-empty">
            <div className="text-2xl mb-1">✅</div>
            <div className="text-sm font-medium text-stone-800 dark:text-stone-200">
              {t.empty}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400 mt-1">
              {t.emptySub}
            </div>
          </div>
        )}
        {!error && !loading && visibleItems.length > 0 && (
          /* El tablero completo es una LISTA vertical, no una rejilla: una
             acción por fila y a todo el ancho, igual que Seguimientos
             (`FollowUpBoard`). En columnas cada tarjeta se quedaba con un
             tercio del ancho, el contexto se apilaba y salían fichas altas
             y estrechas imposibles de barrer con la vista.
             `compact` sigue en rejilla: son 5 tarjetas de una línea dentro
             de un widget de dashboard, no el tablero operativo. */
          <div
            className={compact ? "grid gap-3 sm:grid-cols-2 xl:grid-cols-3" : "space-y-3"}
            data-testid="action-center-board"
          >
            {visibleItems.map((it) => (
              <ActionCard
                key={it.action_id}
                item={it}
                role={role}
                onReload={onReloadAfterMutation}
                onHighlight={highlightActionId === it.action_id}
                lang={lang}
                compact={compact}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * DashboardActionZone — one hook, one AI panel, and a board OR quick
 * navigation depending on the breakpoint.
 *
 * The dashboard-level widget consumed by ExecOverview (admin),
 * MarketingDashboard.Overview (marketing) and ConstructorHub
 * (constructor). Owns a single `useActionCenterData` instance so the
 * dashboard makes exactly two backend requests: GET /actions and
 * POST /actions/next-best-action.
 *
 * Desktop (≥ lg): "Prioridad IA · Top 3" + the compact Action Center —
 *   the richer operational board. Top-3 clicks scroll+highlight the
 *   matching card.
 * Mobile: "Prioridad IA · Top 3" + compact navigation CTAs ("Ver Centro
 *   de acción", "Ver seguimientos") — the compact board is NOT rendered
 *   (it repeated the same top items right below the AI panel and ate
 *   the first screen). Top-3 clicks therefore fall back to the item's
 *   exact primary CTA (PriorityAiPanel does that when the card is not in
 *   the DOM). Role-aware: admin links to /actions and /follow-ups; other
 *   roles use the dashboard-tab callbacks they already had.
 */
export function DashboardActionZone({ lang = "es", onViewAll, onViewFollowUps }) {
  const data = useActionCenterData({
    lang,
    includeInfo: false,
    withNba: true,
  });
  const { user } = useAuth();
  const role = user?.role || "admin";
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const [highlightId, setHighlightId] = useState(null);
  const clearRef = useRef(null);
  useEffect(() => () => clearTimeout(clearRef.current), []);

  const handleFocus = (actionId) => {
    setHighlightId(actionId);
    clearTimeout(clearRef.current);
    clearRef.current = setTimeout(() => {
      setHighlightId((cur) => (cur === actionId ? null : cur));
    }, 2000);
  };

  return (
    <div
      className="space-y-3 mb-6"
      data-testid="dashboard-action-zone"
      data-layout={desktop ? "desktop" : "mobile"}
    >
      <PriorityAiPanel
        nba={data.nba}
        nbaSource={data.nbaSource}
        greeting={data.nbaGreeting}
        itemsByActionId={data.itemsByActionId}
        lang={lang}
        loading={data.loading}
        onFocusActionId={desktop ? handleFocus : undefined}
      />
      {desktop ? (
        <ActionCenter
          compact
          lang={lang}
          items={data.items}
          counts={data.counts}
          loading={data.loading}
          error={data.error}
          reload={data.reloadAll}
          onReloadAfterMutation={data.reloadActions}
          onViewAll={onViewAll}
          highlightActionId={highlightId}
        />
      ) : (
        <DashboardQuickNav
          role={role}
          lang={lang}
          counts={data.counts}
          loading={data.loading}
          error={data.error}
          onReload={data.reloadAll}
          onViewAll={onViewAll}
          onViewFollowUps={onViewFollowUps}
        />
      )}
    </div>
  );
}

/**
 * Mobile-only navigation CTAs under "Prioridad IA". Admin gets real links
 * to the dedicated routes; marketing/constructor keep their dashboard-tab
 * callbacks (their Action Center lives in a tab, /follow-ups is admin-only
 * as a route). Nothing renders for a destination the role cannot reach.
 */
function DashboardQuickNav({
  role, lang, counts, loading, error, onReload, onViewAll, onViewFollowUps,
}) {
  const t = I18N[lang] || I18N.es;
  const pending = (counts?.critical || 0) + (counts?.today || 0) + (counts?.overdue || 0);
  const cls = "flex-1 min-w-0 inline-flex items-center justify-between gap-2 min-h-[44px] px-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 text-sm text-stone-900 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-900";
  const actionsInner = (
    <>
      <span className="inline-flex items-center gap-2 min-w-0">
        <Lightning size={16} weight="fill" className="shrink-0" />
        <span className="truncate">{t.quickActions}</span>
      </span>
      <span className="inline-flex items-center gap-2 shrink-0 text-xs text-stone-500 dark:text-stone-400">
        {pending > 0 && <span data-testid="dashboard-quick-actions-count">{t.quickPending(pending)}</span>}
        <ArrowRight size={14} />
      </span>
    </>
  );
  const followUpsInner = (
    <>
      <span className="inline-flex items-center gap-2 min-w-0">
        <ClockCounterClockwise size={16} className="shrink-0" />
        <span className="truncate">{t.quickFollowUps}</span>
      </span>
      <ArrowRight size={14} className="shrink-0 text-stone-500 dark:text-stone-400" />
    </>
  );
  const showActions = role === "admin" || typeof onViewAll === "function";
  const showFollowUps = role === "admin" || typeof onViewFollowUps === "function";
  // Same feedback the compact board gives on desktop: a loading line while
  // /actions is in flight and an error banner with "Recargar" on failure —
  // the AI panel alone would otherwise read as "Todo en orden".
  const status = error ? (
    <div
      className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300 flex items-start justify-between gap-3"
      data-testid="dashboard-quick-error"
    >
      <span>{t.error}</span>
      {typeof onReload === "function" && (
        <button
          type="button"
          onClick={onReload}
          className="shrink-0 text-xs inline-flex items-center gap-1 underline"
          data-testid="dashboard-quick-reload"
        >
          <ArrowClockwise size={12} />
          {t.reload}
        </button>
      )}
    </div>
  ) : loading ? (
    <div className="text-xs text-stone-500 dark:text-stone-400" data-testid="dashboard-quick-loading">
      {t.loading}
    </div>
  ) : null;
  if (!showActions && !showFollowUps) return status;
  return (
    <div className="space-y-2">
    {status}
    <nav className="flex gap-2" aria-label={t.title} data-testid="dashboard-quick-nav">
      {showActions && (role === "admin" ? (
        <Link to="/actions" className={cls} data-testid="dashboard-quick-actions">
          {actionsInner}
        </Link>
      ) : (
        <button type="button" onClick={onViewAll} className={cls} data-testid="dashboard-quick-actions">
          {actionsInner}
        </button>
      ))}
      {showFollowUps && (role === "admin" ? (
        <Link to="/follow-ups" className={cls} data-testid="dashboard-quick-followups">
          {followUpsInner}
        </Link>
      ) : (
        <button type="button" onClick={onViewFollowUps} className={cls} data-testid="dashboard-quick-followups">
          {followUpsInner}
        </button>
      ))}
    </nav>
    </div>
  );
}

function UrgencyChip({ active, onClick, label, count, style }) {
  const activeStyle = active && style
    ? `${style.badge} border-transparent`
    : active
      ? "bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 border-transparent"
      : "text-stone-700 dark:text-stone-300 border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-900";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors ${activeStyle}`}
      data-testid={`action-center-chip-${label}`}
      aria-pressed={active}
    >
      {label}
      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[10px] bg-white/30 text-current">
        {count}
      </span>
    </button>
  );
}
